import {Component, computed, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, map, startWith} from 'rxjs';

import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {LocationsDataService} from '@on-the-go-web/library/helpers';
import {ENABLE_PREVIEW_FEATURES_TOKEN} from '@on-the-go-web/library';
import {LocationPickerSheet} from '../location-picker-sheet/location-picker-sheet';

@Component({
  selector: 'otg-bottom-nav',
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    TranslatePipe,
  ],
  templateUrl: './bottom-nav.component.html',
  styleUrl: './bottom-nav.component.scss',
})
export class BottomNavComponent {
  private _locationData = inject(LocationsDataService);
  private _bottomSheet = inject(MatBottomSheet);
  private _router = inject(Router);

  currentLocation = this._locationData.currentLocation;

  private _isDataPageUrl = () => this._router.url.split('?')[0].endsWith('/data');

  isDataPage = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this._isDataPageUrl()),
      startWith(this._isDataPageUrl()),
    ),
    {initialValue: this._isDataPageUrl()},
  );

  // routerLinkActive can't tell an item is active here: the link points at
  // /l/current/<section>, but the router redirects that to /l/<locationId>/<section>
  // before it ever becomes the current URL, so their URL trees never match.
  // Compare the resolved section segment instead.
  private _urlSection = () => this._router.url.split('?')[0].split('/')[3];

  activeSection = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this._urlSection()),
      startWith(this._urlSection()),
    ),
    {initialValue: this._urlSection()},
  );

  // Inbox and Publish are preview features; without them they show "Coming soon".
  private readonly _previewEnabled = inject(ENABLE_PREVIEW_FEATURES_TOKEN);

  private _locationId = computed(
    () => this._locationData.currentLocationId() ?? this._locationData.recentLocationIds()?.[0],
  );

  private _failedLogoUrl = signal<string | null>(null);

  showLogo = computed(() => {
    const logoUrl = this.currentLocation()?.logo?.url;
    return !!logoUrl && logoUrl !== this._failedLogoUrl();
  });

  hasMultipleLocations = computed(() => (this._locationData.locations()?.length ?? 0) > 1);

  protected readonly items = [
    {icon: 'trending_up', labelKey: 'analytics', section: 'analytics', enabled: true},
    {icon: 'inbox', labelKey: 'inbox', section: 'inbox', enabled: this._previewEnabled},
    {icon: 'publish', labelKey: 'publish', section: 'publish', enabled: this._previewEnabled},
  ];

  onLogoError(): void {
    const logoUrl = this.currentLocation()?.logo?.url;
    if (logoUrl) {
      this._failedLogoUrl.set(logoUrl);
    }
  }

  openLocationPicker(event: Event): void {
    if (!this.hasMultipleLocations()) return;
    event.preventDefault();
    this._bottomSheet.open(LocationPickerSheet, {
      data: {
        locations: this._locationData.locations() ?? [],
        currentLocationId: this._locationId() ?? null,
      },
    });
  }
}
